import React, { useEffect, useState } from 'react'
import { useGlobal } from '../../core/GlobalContext'
import { dealFooterParser } from '../../utils/deals/dealFooterParsers'
import { dealPurchaseParser } from '../../utils/deals/dealPurchaseParsers'
import { dealSummaryParser } from '../../utils/deals/dealSummaryParsers'
import { currencyParser, customParseFloat } from '../../utils/misc'
import ConfirmationOfSigning from '../confirmationOfSigning'
import DealFooter from '../dealFooter'
import DealPurchase from '../dealPurchase'
import DealSummary from '../dealSummary'

import * as styles from './DealCard.css'

/**
 * Redesigned Discover deal card (IncomeBazaar / high_yield look).
 * Presentation only — purchase (DealPurchase) and signing/summary swap
 * logic are unchanged; everything else is derived from the already-parsed
 * `deal` object without touching data flow.
 */
export const DealCard = ({ deal, innerRef }) => {
  const [showSummary, setShowSummary] = useState(false)
  const [showSigning, setShowSigning] = useState(false)
  const [purchaseAmount, setPurchaseAmount] = useState('')
  const [dealSummary, setDealSummary] = useState()
  const [dealFooter, setDealFooter] = useState()
  const [dealPurchase, setDealPurchase] = useState()

  const {
    documentsState: { docUrlMap },
  } = useGlobal()

  useEffect(() => {
    setDealPurchase(dealPurchaseParser(deal))
    setDealFooter(dealFooterParser(deal))
  }, [deal])

  useEffect(() => {
    setDealSummary(dealSummaryParser(deal, purchaseAmount))
  }, [deal, purchaseAmount])

  const buyDeal = purchaseValidation => {
    console.log(purchaseValidation)
    setShowSigning(true)
  }

  // Progress fill escalates with subscription (navy → amber → red), IB palette.
  const get_color_based_on_progress = progress =>
    progress < 0.5 ? 'var(--ib-navy)' : progress < 0.8 ? 'var(--ib-amber)' : 'var(--ib-red)'

  const deal_progress = customParseFloat(deal?.soldAmount / deal?.totalAmount)
  const deal_progress_percentage = Math.floor(deal_progress * 100)
  const progress_color = get_color_based_on_progress(deal_progress)

  const available = deal?.totalAmount - deal?.soldAmount
  const isSoldOut = deal?.totalAmount != null && available <= 0

  // Status badge: sold out → subscribed, ≥80% → closing, else open.
  const status = isSoldOut
    ? { cls: styles.badgeFull, label: 'Fully subscribed' }
    : deal_progress >= 0.8
    ? { cls: styles.badgeClosing, label: 'Closing soon' }
    : { cls: styles.badgeOpen, label: 'Open' }

  // Facts strip — type-aware so debt/lease cards stay meaningful.
  const facts =
    deal?.totalAmount != null
      ? [
          { k: 'Deal size', v: currencyParser(deal?.totalAmount) },
          { k: 'Min. investment', v: currencyParser(deal?.minPurchaseAmount) },
        ]
      : deal?.availableLots != null
      ? [
          { k: 'Available lots', v: deal?.availableLots },
          { k: 'Amount / lot', v: currencyParser(deal?.amountPerLot) },
        ]
      : [{ k: 'Min. investment', v: currencyParser(deal?.minPurchaseAmount) }]

  const logoUrl = docUrlMap[deal?.logo]?.url

  return (
    <div className={styles.dealCardContainer}>
      <div
        className={`${styles.dealCard} ${deal?.dealCode?.slice(0, 1) === 'D' ? styles.debt : styles.std}`}
        ref={innerRef}>
        <div className={styles.watermark} style={{ backgroundImage: `url(${logoUrl})` }} />
        <div className={styles.dealCardContent}>
          {showSigning ? (
            <ConfirmationOfSigning
              purchase={dealPurchase}
              purchaseAmount={purchaseAmount?.replace(/,/g, '')}
              setShowSigning={setShowSigning}
            />
          ) : showSummary ? (
            <DealSummary summary={dealSummary} setShowSummary={setShowSummary} />
          ) : (
            <>
              <div className={styles.cTop}>
                <div className={styles.logoBox}>
                  <img src={logoUrl} className={styles.dealLogo} alt={`${deal?.dealCode?.slice(3, 14)}`} />
                </div>
                <span className={`${styles.badge} ${status.cls}`}>{status.label}</span>
              </div>

              <div className={styles.cCode}>{deal?.dealCode}</div>

              <div className={styles.cHero}>
                <div className={styles.irr}>
                  <span className={styles.heroNum}>
                    {deal?.netIRR}
                    <span className={styles.heroUnit}>%</span>
                  </span>
                  <div className={styles.heroLbl}>Net IRR p.a.</div>
                </div>
                {deal?.duration ? (
                  <>
                    <div className={styles.heroSep} />
                    <div className={styles.tenure}>
                      <span className={styles.heroNumSm}>
                        {deal?.duration}
                        <span className={styles.heroUnitSm}>d</span>
                      </span>
                      <div className={styles.heroLbl}>Tenure</div>
                    </div>
                  </>
                ) : null}
              </div>

              {deal?.progressBar && (
                <div className={styles.prog}>
                  <div className={styles.track}>
                    <div
                      className={styles.fill}
                      style={{ width: `${deal_progress_percentage}%`, background: progress_color }}
                    />
                  </div>
                  <div className={styles.progCaption}>
                    <span>{deal_progress_percentage}% subscribed</span>
                    <span>
                      <b>{currencyParser(available)}</b> of {currencyParser(deal?.totalAmount)} left
                    </span>
                  </div>
                </div>
              )}

              <div className={styles.facts}>
                {facts.map((f, i) => (
                  <div key={f.k} className={`${styles.fact} ${i % 2 ? styles.factRight : ''}`}>
                    <div className={styles.factKey}>{f.k}</div>
                    <div className={styles.factVal}>{f.v}</div>
                  </div>
                ))}
              </div>

              <DealPurchase
                purchase={dealPurchase}
                purchaseAmount={purchaseAmount}
                setPurchaseAmount={setPurchaseAmount}
                buyDeal={buyDeal}
                soldOut={isSoldOut}
              />
            </>
          )}
          {!showSigning && (
            <DealFooter
              footer={dealFooter}
              showSummary={showSummary}
              setShowSummary={setShowSummary}
              purchaseAmount={purchaseAmount}
            />
          )}
        </div>
      </div>
    </div>
  )
}
